import {
    MakeLogicType,
    actions,
    connect,
    defaults,
    events,
    kea,
    key,
    listeners,
    path,
    props,
    reducers,
    selectors,
} from 'kea'
import { loaders } from 'kea-loaders'
import { actionToUrl, router, urlToAction } from 'kea-router'
import { subscriptions } from 'kea-subscriptions'
import posthog from 'posthog-js'

import api from 'lib/api'
import {
    ErrorEventProperties,
    ErrorEventType,
    ErrorTrackingFingerprint,
    ErrorTrackingSpikeEvent,
} from 'lib/components/Errors/types'
import { SetupTaskId, globalSetupLogic } from 'lib/components/ProductSetup'
import { Dayjs, dayjs } from 'lib/dayjs'
import { getCurrentTeamId } from 'lib/utils/getAppContext'
import { objectsEqual } from 'lib/utils/objects'
import { MaxContextInput, createMaxContextHelpers } from 'scenes/max/maxTypes'
import { Scene } from 'scenes/sceneTypes'
import { Params } from 'scenes/sceneTypes'
import { urls } from 'scenes/urls'

import { SIDE_PANEL_CONTEXT_KEY, SidePanelSceneContext } from '~/layout/navigation-3000/sidepanel/types'
import {
    DateRange,
    ErrorTrackingIssue,
    ErrorTrackingIssueAggregations,
    ErrorTrackingRelationalIssue,
} from '~/queries/schema/schema-general'
import { ActivityScope, Breadcrumb, IntegrationType, UniversalFiltersGroup } from '~/types'

import { signalsReportsList } from 'products/signals/frontend/generated/api'
import type { SignalReportApi } from 'products/signals/frontend/generated/api.schemas'
import { SignalSourceProductApi } from 'products/signals/frontend/generated/api.schemas'

import type {
    ErrorTrackingExternalReference,
    ErrorTrackingIssueAssignee,
    ErrorTrackingIssueCohort,
    EventsQuery,
} from '../../../../../frontend/src/queries/schema/schema-general'
import { issueActionsLogic } from '../../components/IssueActions/issueActionsLogic'
import {
    DEFAULT_DATE_RANGE,
    issueFiltersLogic,
    triggerFilterActions,
    updateFilterSearchParams,
} from '../../components/IssueFilters/issueFiltersLogic'
import { errorTrackingIssueEventsQuery, errorTrackingIssueQuery } from '../../queries'
import { syncSearchParams } from '../../utils'
import { ERROR_TRACKING_DETAILS_RESOLUTION, dateRangeToIsoBounds } from '../../utils'
import {
    DEFAULT_CATEGORY,
    ErrorTrackingIssueSceneCategory,
    VALID_CATEGORIES,
    errorTrackingIssueSceneConfigurationLogic,
} from './errorTrackingIssueSceneConfigurationLogic'

export interface ErrorTrackingIssueSceneLogicProps {
    id: ErrorTrackingIssue['id']
    fingerprint?: string
    timestamp?: string
}

export type ErrorTrackingIssueStatus = ErrorTrackingIssue['status']

export const ERROR_TRACKING_ISSUE_SCENE_LOGIC_KEY = 'ErrorTrackingIssueScene'

/** An issue realistically groups into one or two reports. This only stops the endpoint's 100-report
 * default page from serializing far more than the panel will ever show. */
const LINKED_REPORTS_LIMIT = 10

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface errorTrackingIssueSceneLogicValues {
    category: ErrorTrackingIssueSceneCategory // errorTrackingIssueSceneConfigurationLogic
    dateRange: DateRange // issueFiltersLogic
    filterGroup: UniversalFiltersGroup // issueFiltersLogic
    filterTestAccounts: boolean // issueFiltersLogic
    searchQuery: string // issueFiltersLogic
    aggregations: ErrorTrackingIssueAggregations | undefined
    breadcrumbs: Breadcrumb[]
    eventsQuery: EventsQuery
    eventsQueryKey: string
    firstSeen: Dayjs | null
    initialEvent: ErrorEventType | null
    initialEventLoading: boolean
    initialEventTimestamp: string | null
    issue: ErrorTrackingRelationalIssue | null
    issueFingerprints: ErrorTrackingFingerprint[]
    issueFingerprintsLoading: boolean
    issueId: string
    issueLoading: boolean
    lastSeen: Dayjs | null
    linkedReports: SignalReportApi[]
    linkedReportsLoading: boolean
    listDateRange: DateRange | null
    maxContext: MaxContextInput[]
    mobileDetailOpen: boolean
    properties: ErrorEventProperties | null
    selectedEvent: ErrorEventType | null
    sidePanelContext: SidePanelSceneContext
    spikeEvents: ErrorTrackingSpikeEvent[]
    spikeEventsLoading: boolean
    summary: ErrorTrackingIssueSummary | null
    summaryLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface errorTrackingIssueSceneLogicActions {
    setCategory: (category: ErrorTrackingIssueSceneCategory) => {
        category: ErrorTrackingIssueSceneCategory
    } // errorTrackingIssueSceneConfigurationLogic
    updateIssueAssignee: (
        id: string,
        assignee: ErrorTrackingIssueAssignee | null
    ) => {
        assignee: ErrorTrackingIssueAssignee | null
        id: string
    } // issueActionsLogic
    updateIssueDescription: (
        id: string,
        description: string
    ) => {
        description: string
        id: string
    } // issueActionsLogic
    updateIssueName: (
        id: string,
        name: string
    ) => {
        id: string
        name: string
    } // issueActionsLogic
    updateIssueStatus: (
        id: string,
        status: import('~/queries/schema').ErrorTrackingIssueStatus
    ) => {
        id: string
        status: import('~/queries/schema').ErrorTrackingIssueStatus
    } // issueActionsLogic
    setDateRange: (dateRange: DateRange) => {
        dateRange: DateRange
    } // issueFiltersLogic
    setFilterGroup: (filterGroup: UniversalFiltersGroup) => {
        filterGroup: UniversalFiltersGroup
    } // issueFiltersLogic
    setFilterTestAccounts: (filterTestAccounts: boolean) => {
        filterTestAccounts: boolean
    } // issueFiltersLogic
    setSearchQuery: (searchQuery: string) => {
        searchQuery: string
    } // issueFiltersLogic
    createExternalReference: (
        integrationId: IntegrationType['id'],
        config: Record<string, string>
    ) => {
        config: Record<string, string>
        integrationId: number
    }
    createExternalReferenceFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    createExternalReferenceSuccess: (
        issue: {
            assignee: ErrorTrackingIssueAssignee | null
            cohort?: ErrorTrackingIssueCohort | undefined
            description: string | null
            external_issues: ErrorTrackingExternalReference[]
            first_seen: string
            id: string
            name: string | null
            status: ErrorTrackingIssueStatus
        } | null,
        payload?: {
            config: Record<string, string>
            integrationId: number
        }
    ) => {
        issue: {
            assignee: ErrorTrackingIssueAssignee | null
            cohort?: ErrorTrackingIssueCohort | undefined
            description: string | null
            external_issues: ErrorTrackingExternalReference[]
            first_seen: string
            id: string
            name: string | null
            status: ErrorTrackingIssueStatus
        } | null
        payload?: {
            config: Record<string, string>
            integrationId: number
        }
    }
    loadInitialEvent: (timestamp: string) => {
        timestamp: string
    }
    loadInitialEventFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadInitialEventSuccess: (
        initialEvent: ErrorEventType | null,
        payload?: {
            timestamp: string
        }
    ) => {
        initialEvent: ErrorEventType | null
        payload?: {
            timestamp: string
        }
    }
    loadIssue: () => {
        value: true
    }
    loadIssueFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadIssueFingerprints: () => any
    loadIssueFingerprintsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadIssueFingerprintsSuccess: (
        issueFingerprints: ErrorTrackingFingerprint[],
        payload?: any
    ) => {
        issueFingerprints: ErrorTrackingFingerprint[]
        payload?: any
    }
    loadIssueSuccess: (
        issue: ErrorTrackingRelationalIssue,
        payload?: {
            value: true
        }
    ) => {
        issue: ErrorTrackingRelationalIssue
        payload?: {
            value: true
        }
    }
    loadLinkedReports: () => any
    loadLinkedReportsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadLinkedReportsSuccess: (
        linkedReports: SignalReportApi[],
        payload?: any
    ) => {
        linkedReports: SignalReportApi[]
        payload?: any
    }
    loadSpikeEvents: () => any
    loadSpikeEventsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadSpikeEventsSuccess: (
        spikeEvents: ErrorTrackingSpikeEvent[],
        payload?: any
    ) => {
        spikeEvents: ErrorTrackingSpikeEvent[]
        payload?: any
    }
    loadSummary: () => {
        value: true
    }
    loadSummaryFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadSummarySuccess: (
        summary: ErrorTrackingIssueSummary | null,
        payload?: {
            value: true
        }
    ) => {
        summary: ErrorTrackingIssueSummary | null
        payload?: {
            value: true
        }
    }
    selectEvent: (event: ErrorEventType | null) => {
        event: ErrorEventType | null
    }
    setInitialEventTimestamp: (timestamp: string | null) => {
        timestamp: string | null
    }
    setIssue: (issue: ErrorTrackingRelationalIssue) => {
        issue: ErrorTrackingRelationalIssue
    }
    setIssueFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    setIssueSuccess: (
        issue: ErrorTrackingRelationalIssue,
        payload?: {
            issue: ErrorTrackingRelationalIssue
        }
    ) => {
        issue: ErrorTrackingRelationalIssue
        payload?: {
            issue: ErrorTrackingRelationalIssue
        }
    }
    setLastSeen: (lastSeen: string) => {
        lastSeen: string
    }
    setListDateRange: (dateRange: DateRange) => {
        dateRange: DateRange
    }
    setMobileDetailOpen: (mobileDetailOpen: boolean) => {
        mobileDetailOpen: boolean
    }
    updateAssignee: (assignee: ErrorTrackingIssue['assignee']) => {
        assignee: ErrorTrackingIssueAssignee | null
    }
    updateAssigneeFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateAssigneeSuccess: (
        issue: {
            assignee: ErrorTrackingIssueAssignee | null
            cohort?: ErrorTrackingIssueCohort | undefined
            description: string | null
            external_issues?: ErrorTrackingExternalReference[] | undefined
            first_seen: string
            id: string
            name: string | null
            status: ErrorTrackingIssueStatus
        } | null,
        payload?: {
            assignee: ErrorTrackingIssueAssignee | null
        }
    ) => {
        issue: {
            assignee: ErrorTrackingIssueAssignee | null
            cohort?: ErrorTrackingIssueCohort | undefined
            description: string | null
            external_issues?: ErrorTrackingExternalReference[] | undefined
            first_seen: string
            id: string
            name: string | null
            status: ErrorTrackingIssueStatus
        } | null
        payload?: {
            assignee: ErrorTrackingIssueAssignee | null
        }
    }
    updateDescription: (description: string) => {
        description: string
    }
    updateDescriptionFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateDescriptionSuccess: (
        issue: {
            assignee: ErrorTrackingIssueAssignee | null
            cohort?: ErrorTrackingIssueCohort | undefined
            description: string
            external_issues?: ErrorTrackingExternalReference[] | undefined
            first_seen: string
            id: string
            name: string | null
            status: ErrorTrackingIssueStatus
        } | null,
        payload?: {
            description: string
        }
    ) => {
        issue: {
            assignee: ErrorTrackingIssueAssignee | null
            cohort?: ErrorTrackingIssueCohort | undefined
            description: string
            external_issues?: ErrorTrackingExternalReference[] | undefined
            first_seen: string
            id: string
            name: string | null
            status: ErrorTrackingIssueStatus
        } | null
        payload?: {
            description: string
        }
    }
    updateName: (name: string) => {
        name: string
    }
    updateNameFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateNameSuccess: (
        issue: {
            assignee: ErrorTrackingIssueAssignee | null
            cohort?: ErrorTrackingIssueCohort | undefined
            description: string | null
            external_issues?: ErrorTrackingExternalReference[] | undefined
            first_seen: string
            id: string
            name: string
            status: ErrorTrackingIssueStatus
        } | null,
        payload?: {
            name: string
        }
    ) => {
        issue: {
            assignee: ErrorTrackingIssueAssignee | null
            cohort?: ErrorTrackingIssueCohort | undefined
            description: string | null
            external_issues?: ErrorTrackingExternalReference[] | undefined
            first_seen: string
            id: string
            name: string
            status: ErrorTrackingIssueStatus
        } | null
        payload?: {
            name: string
        }
    }
    updateStatus: (status: ErrorTrackingIssue['status']) => {
        status: ErrorTrackingIssueStatus
    }
    updateStatusFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateStatusSuccess: (
        issue: {
            assignee: ErrorTrackingIssueAssignee | null
            cohort?: ErrorTrackingIssueCohort | undefined
            description: string | null
            external_issues?: ErrorTrackingExternalReference[] | undefined
            first_seen: string
            id: string
            name: string | null
            status: ErrorTrackingIssueStatus
        } | null,
        payload?: {
            status: ErrorTrackingIssueStatus
        }
    ) => {
        issue: {
            assignee: ErrorTrackingIssueAssignee | null
            cohort?: ErrorTrackingIssueCohort | undefined
            description: string | null
            external_issues?: ErrorTrackingExternalReference[] | undefined
            first_seen: string
            id: string
            name: string | null
            status: ErrorTrackingIssueStatus
        } | null
        payload?: {
            status: ErrorTrackingIssueStatus
        }
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface errorTrackingIssueSceneLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        breadcrumbs: (
            issue: ErrorTrackingRelationalIssue | null,
            listDateRange: DateRange | null,
            filterTestAccounts: boolean,
            filterGroup: UniversalFiltersGroup,
            searchQuery: string
        ) => Breadcrumb[]
        sidePanelContext: (id: string) => SidePanelSceneContext
        issueId: (id: string) => string
        firstSeen: (issue: ErrorTrackingRelationalIssue | null) => Dayjs | null
        aggregations: (summary: ErrorTrackingIssueSummary | null) => ErrorTrackingIssueAggregations | undefined
        eventsQuery: (
            issueId: string,
            filterTestAccounts: boolean,
            searchQuery: string,
            filterGroup: UniversalFiltersGroup,
            dateRange: DateRange
        ) => EventsQuery
        eventsQueryKey: (eventsQuery: EventsQuery) => string
        maxContext: (issue: ErrorTrackingRelationalIssue | null, issueId: string) => MaxContextInput[]
    }
}

export type errorTrackingIssueSceneLogicType = MakeLogicType<
    errorTrackingIssueSceneLogicValues,
    errorTrackingIssueSceneLogicActions,
    ErrorTrackingIssueSceneLogicProps,
    errorTrackingIssueSceneLogicMeta
>

export const errorTrackingIssueSceneLogic = kea<errorTrackingIssueSceneLogicType>([
    path((key) => [
        'products',
        'error_tracking',
        'scenes',
        'ErrorTrackingIssueScene',
        'errorTrackingIssueSceneLogic',
        key,
    ]),
    props({} as ErrorTrackingIssueSceneLogicProps),
    key((props) => props.id),

    connect(() => ({
        values: [
            issueFiltersLogic({ logicKey: ERROR_TRACKING_ISSUE_SCENE_LOGIC_KEY }),
            ['dateRange', 'filterTestAccounts', 'filterGroup', 'searchQuery'],
            errorTrackingIssueSceneConfigurationLogic,
            ['category'],
        ],
        actions: [
            issueFiltersLogic({ logicKey: ERROR_TRACKING_ISSUE_SCENE_LOGIC_KEY }),
            ['setDateRange', 'setFilterTestAccounts', 'setFilterGroup', 'setSearchQuery'],
            issueActionsLogic,
            ['updateIssueAssignee', 'updateIssueStatus', 'updateIssueName', 'updateIssueDescription'],
            errorTrackingIssueSceneConfigurationLogic,
            ['setCategory'],
        ],
    })),

    actions({
        loadIssue: true,
        loadSummary: true,
        loadInitialEvent: (timestamp: string) => ({ timestamp }),
        setMobileDetailOpen: (mobileDetailOpen: boolean) => ({ mobileDetailOpen }),
        setInitialEventTimestamp: (timestamp: string | null) => ({ timestamp }),
        setIssue: (issue: ErrorTrackingRelationalIssue) => ({ issue }),
        setLastSeen: (lastSeen: string) => ({ lastSeen }),
        selectEvent: (event: ErrorEventType | null) => ({
            event,
        }),
        createExternalReference: (integrationId: IntegrationType['id'], config: Record<string, string>) => ({
            integrationId,
            config,
        }),
        updateAssignee: (assignee: ErrorTrackingIssue['assignee']) => ({ assignee }),
        updateStatus: (status: ErrorTrackingIssue['status']) => ({ status }),
        updateName: (name: string) => ({ name }),
        updateDescription: (description: string) => ({ description }),
        setListDateRange: (dateRange: DateRange) => ({ dateRange }),
    }),

    defaults({
        issue: null as ErrorTrackingRelationalIssue | null,
        summary: null as ErrorTrackingIssueSummary | null,
        properties: null as ErrorEventProperties | null,
        lastSeen: null as Dayjs | null,
        initialEvent: null as ErrorEventType | null,
        selectedEvent: null as ErrorEventType | null,
        mobileDetailOpen: false as boolean,
        initialEventTimestamp: null as string | null,
        initialEventLoading: true as boolean,
        listDateRange: null as DateRange | null,
    }),

    reducers(({ values }) => ({
        summary: {},
        lastSeen: {
            setLastSeen: (prevLastSeen, { lastSeen }) => {
                const lastSeenDayjs = dayjs(lastSeen)
                if (!prevLastSeen || prevLastSeen.isBefore(lastSeenDayjs)) {
                    return lastSeenDayjs
                }
                return prevLastSeen
            },
        },
        initialEventTimestamp: {
            // A malformed `timestamp` URL param (truncated/mangled shared links, crawlers) would
            // otherwise be stored and fed to getNarrowDateRange, where dayjs().toISOString() throws
            // a RangeError on an Invalid Date. Ignore invalid values so we fall back to the valid
            // last_seen/first_seen the server provides, exactly as if no timestamp were passed.
            setInitialEventTimestamp: (state, { timestamp }) => {
                if (!state && timestamp && dayjs(timestamp).isValid()) {
                    return timestamp
                }
                return state
            },
        },
        selectedEvent: {
            selectEvent: (_, { event }) => {
                if (!event && values.initialEvent) {
                    return values.initialEvent
                }
                return event
            },
        },
        mobileDetailOpen: {
            setMobileDetailOpen: (_, { mobileDetailOpen }) => mobileDetailOpen,
        },
        listDateRange: {
            setListDateRange: (_, { dateRange }) => dateRange,
        },
    })),

    loaders(({ values, actions, props }) => ({
        issue: {
            setIssue: ({ issue }) => issue,
            loadIssue: async () => await api.errorTracking.getIssue(props.id, props.fingerprint),
            createExternalReference: async ({ integrationId, config }) => {
                if (values.issue) {
                    const response = await api.errorTracking.createExternalReference(props.id, integrationId, config)
                    posthog.capture('error_tracking_issue_pushed', {
                        issue_id: props.id,
                        destination: response.integration.kind,
                    })
                    const externalIssues = values.issue.external_issues ?? []
                    return { ...values.issue, external_issues: [...externalIssues, response] }
                }
                return null
            },
            updateAssignee: ({ assignee }) => {
                if (values.issue) {
                    return { ...values.issue, assignee }
                }
                return values.issue
            },
            updateStatus: ({ status }) => {
                if (values.issue) {
                    return { ...values.issue, status }
                }
                return values.issue
            },
            updateName: ({ name }) => {
                if (values.issue) {
                    return { ...values.issue, name }
                }
                return values.issue
            },
            updateDescription: ({ description }) => {
                if (values.issue) {
                    return { ...values.issue, description }
                }
                return values.issue
            },
        },
        initialEvent: {
            loadInitialEvent: async ({ timestamp }) => {
                // Guard the narrow-range formatting below: an invalid timestamp makes
                // getNarrowDateRange throw a RangeError. Treat it as "no initial event".
                if (!dayjs(timestamp).isValid()) {
                    return null
                }
                const response = await api.query(
                    errorTrackingIssueQuery({
                        issueId: props.id,
                        dateRange: getNarrowDateRange(timestamp),
                        filterTestAccounts: false,
                        withAggregations: false,
                        withFirstEvent: false,
                        withLastEvent: true,
                    }),
                    { refresh: 'blocking' }
                )
                const positionEvent = response.results[0]?.last_event
                if (!positionEvent) {
                    return null
                }
                const initialEvent: ErrorEventType = {
                    event: '$exception',
                    uuid: positionEvent.uuid,
                    distinct_id: positionEvent.distinct_id,
                    timestamp: positionEvent.timestamp,
                    person: { distinct_ids: [], properties: {} },
                    properties: JSON.parse(positionEvent.properties),
                }
                if (!values.selectedEvent) {
                    actions.selectEvent(initialEvent)
                }
                return initialEvent
            },
        },
        summary: {
            loadSummary: async () => {
                const response = await api.query(
                    errorTrackingIssueQuery({
                        issueId: props.id,
                        dateRange: values.dateRange,
                        filterTestAccounts: values.filterTestAccounts,
                        filterGroup: values.filterGroup,
                        searchQuery: values.searchQuery,
                        volumeResolution: ERROR_TRACKING_DETAILS_RESOLUTION,
                        withAggregations: true,
                        withFirstEvent: true,
                        withLastEvent: true,
                    }),
                    { refresh: 'blocking' }
                )
                if (!response.results.length) {
                    return null
                }
                return toErrorTrackingIssueSummary(response.results[0])
            },
        },
        issueFingerprints: [
            [] as ErrorTrackingFingerprint[],
            {
                loadIssueFingerprints: async () => await api.errorTracking.fingerprints.list(props.id),
            },
        ],
        linkedReports: [
            [] as SignalReportApi[],
            {
                // Error tracking tags its signals with the issue id as `source_id`, so this filter
                // walks the link from the issue back to the reports its signals fed.
                loadLinkedReports: async (): Promise<SignalReportApi[]> => {
                    try {
                        const response = await signalsReportsList(getCurrentTeamId().toString(), {
                            source_id: props.id,
                            source_product: SignalSourceProductApi.ErrorTracking,
                            // The endpoint's default ordering leads with the viewer's own review
                            // queue, which is an inbox concern rather than this pane's.
                            ordering: '-updated_at',
                            limit: LINKED_REPORTS_LIMIT,
                        })
                        return response.results
                    } catch (error) {
                        // Supplementary context, so it degrades quietly: kea-loaders toasts a failed
                        // `load*` action, and a signals outage must not do that on every issue page.
                        console.error('Failed to load linked reports:', error)
                        return []
                    }
                },
            },
        ],
        spikeEvents: [
            [] as ErrorTrackingSpikeEvent[],
            {
                loadSpikeEvents: async () => {
                    const { dateFrom, dateTo } = dateRangeToIsoBounds(values.dateRange)
                    const response = await api.errorTracking.getSpikeEvents({
                        issueIds: [props.id],
                        dateFrom,
                        dateTo,
                    })
                    return response.results
                },
            },
        ],
    })),

    selectors(({ actions }) => ({
        breadcrumbs: [
            (s) => [s.issue, s.listDateRange, s.filterTestAccounts, s.filterGroup, s.searchQuery],
            (
                issue: ErrorTrackingRelationalIssue | null,
                listDateRange: DateRange | null,
                filterTestAccounts: boolean,
                filterGroup: UniversalFiltersGroup,
                searchQuery: string
            ): Breadcrumb[] => {
                const exceptionType: string = issue?.name || 'Issue'
                // Use the original list date range for back navigation
                const urlParams = updateFilterSearchParams(
                    {},
                    {
                        dateRange: listDateRange ?? DEFAULT_DATE_RANGE,
                        filterTestAccounts,
                        filterGroup,
                        searchQuery,
                    }
                )

                return [
                    {
                        key: Scene.ErrorTracking,
                        name: 'Error tracking',
                        path: urls.errorTracking(urlParams),
                        iconType: 'error_tracking',
                    },
                    {
                        key: [Scene.ErrorTrackingIssue, exceptionType],
                        name: exceptionType,
                        onRename: async (name: string) => actions.updateName(name),
                        iconType: 'error_tracking',
                    },
                ]
            },
        ],

        [SIDE_PANEL_CONTEXT_KEY]: [
            (_, p) => [p.id],
            (issueId: string): SidePanelSceneContext => {
                return {
                    activity_scope: ActivityScope.ERROR_TRACKING_ISSUE,
                    activity_item_id: issueId,
                }
            },
        ],
        issueId: [(_, p) => [p.id], (id: string) => id],

        firstSeen: [
            (s) => [s.issue],
            (issue: ErrorTrackingRelationalIssue | null) => (issue ? dayjs(issue.first_seen) : null),
        ],

        aggregations: [(s) => [s.summary], (summary: ErrorTrackingIssueSummary | null) => summary?.aggregations],

        eventsQuery: [
            (s) => [s.issueId, s.filterTestAccounts, s.searchQuery, s.filterGroup, s.dateRange],
            (
                issueId: string,
                filterTestAccounts: boolean,
                searchQuery: string,
                filterGroup: UniversalFiltersGroup,
                dateRange: DateRange
            ) =>
                errorTrackingIssueEventsQuery({
                    issueId,
                    filterTestAccounts,
                    filterGroup,
                    searchQuery,
                    dateRange,
                    columns: ['*', 'timestamp', 'person'],
                }),
            // Preserve the query identity when selector inputs are deep-equal to avoid remounting the events table.
            { resultEqualityCheck: objectsEqual },
        ],

        // The key is the kea key of eventsSourceLogic, whose dataNodeLogic is wired in connect()
        // once per key: it MUST change when the query content changes (that's how a new query
        // reaches the data layer) and must NOT change otherwise — every key change unmounts and
        // remounts the entire events table tree.
        eventsQueryKey: [
            (s) => [s.eventsQuery],
            (eventsQuery: import('~/queries/schema').EventsQuery): string => JSON.stringify(eventsQuery),
        ],

        maxContext: [
            (s) => [s.issue, s.issueId],
            (issue: ErrorTrackingRelationalIssue | null, issueId: string): MaxContextInput[] => {
                if (!issueId) {
                    return []
                }
                return [
                    createMaxContextHelpers.errorTrackingIssue({
                        id: issueId,
                        name: issue?.name ?? null,
                    }),
                ]
            },
        ],
    })),

    subscriptions(({ actions }) => ({
        initialEventTimestamp: (value: string | null, oldValue: string | null) => {
            if (!oldValue && value) {
                actions.loadInitialEvent(value)
            }
        },
    })),

    listeners(({ props, values, actions }) => {
        return {
            setDateRange: () => {
                actions.loadSummary()
                actions.loadSpikeEvents()
            },
            setFilterGroup: actions.loadSummary,
            setFilterTestAccounts: actions.loadSummary,
            setSearchQuery: actions.loadSummary,
            loadSummarySuccess: ({ summary }: { summary: ErrorTrackingIssueSummary | null }) => {
                if (summary && summary.last_seen) {
                    actions.setLastSeen(summary.last_seen)
                    actions.setInitialEventTimestamp(summary.last_seen)
                } else {
                    actions.setInitialEventTimestamp(values.issue?.first_seen ?? null)
                }
            },
            loadIssueFailure: ({ errorObject: { status, data } }) => {
                if (status == 308 && 'issue_id' in data) {
                    router.actions.replace(urls.errorTrackingIssue(data.issue_id))
                }
            },
            updateName: ({ name }) => actions.updateIssueName(props.id, name),
            updateDescription: ({ description }) => actions.updateIssueDescription(props.id, description),
            updateAssignee: ({ assignee }) => actions.updateIssueAssignee(props.id, assignee),
            updateStatus: ({ status }) => actions.updateIssueStatus(props.id, status),
            selectEvent: ({ event }) => {
                if (event) {
                    router.actions.replace(
                        router.values.currentLocation.pathname,
                        {
                            ...router.values.searchParams,
                            timestamp: event.timestamp,
                        },
                        router.values.hashParams
                    )
                }
            },
            [issueActionsLogic.actionTypes.mutationSuccess]: ({ mutationName }) => {
                if (mutationName === 'mergeIssues') {
                    actions.loadIssue()
                    actions.loadSummary()
                    actions.loadIssueFingerprints()
                }
                if (mutationName === 'createIssueCohort') {
                    actions.loadIssue()
                }
            },
        }
    }),

    events(({ props, actions }) => ({
        afterMount: () => {
            actions.loadIssue()
            actions.setInitialEventTimestamp(props.timestamp ?? null)
            actions.loadSummary()
            actions.loadIssueFingerprints()
            actions.loadSpikeEvents()
            actions.loadLinkedReports()
            globalSetupLogic.findMounted()?.actions.markTaskAsCompleted(SetupTaskId.ViewFirstError)
        },
    })),

    urlToAction(({ actions, values }) => {
        return {
            '**/error_tracking/:id': (_, params) => {
                if (values.listDateRange == null) {
                    actions.setListDateRange(params.dateRange ?? DEFAULT_DATE_RANGE)
                }
                triggerFilterActions(params, values, actions)
                const tab = params.tab as ErrorTrackingIssueSceneCategory | undefined
                const category = tab && VALID_CATEGORIES.includes(tab) ? tab : DEFAULT_CATEGORY
                if (category !== values.category) {
                    actions.setCategory(category)
                }
            },
        }
    }),

    actionToUrl(({ values }) => {
        const buildURL = (): ReturnType<typeof syncSearchParams> =>
            syncSearchParams(router, (params: Params) => {
                updateFilterSearchParams(params, values)
                if (values.category === DEFAULT_CATEGORY) {
                    delete params.tab
                } else {
                    params.tab = values.category
                }
                return params
            })

        return {
            setDateRange: buildURL,
            setFilterGroup: buildURL,
            setSearchQuery: buildURL,
            setFilterTestAccounts: buildURL,
            setCategory: buildURL,
        }
    }),
])

function getNarrowDateRange(timestamp: Dayjs | string): DateRange {
    const firstSeen = dayjs(timestamp)
    return {
        date_from: firstSeen.subtract(1, 'hour').toISOString(),
        date_to: firstSeen.add(1, 'hour').toISOString(),
    }
}

export type ErrorTrackingIssueSummary = {
    last_seen?: string
    first_seen?: string
    first_event_uuid?: string
    last_event_uuid?: string
    aggregations: ErrorTrackingIssueAggregations
}

export function toErrorTrackingIssueSummary(
    summary: Pick<ErrorTrackingIssue, 'first_seen' | 'last_seen' | 'first_event' | 'last_event' | 'aggregations'>
): ErrorTrackingIssueSummary | null {
    if (!summary.aggregations) {
        return null
    }

    return {
        first_seen: summary.first_seen,
        last_seen: summary.last_seen,
        first_event_uuid: summary.first_event?.uuid,
        last_event_uuid: summary.last_event?.uuid,
        aggregations: summary.aggregations,
    }
}
